import {
  DrawingType,
  IMPERIAL_SCALE_OPTIONS,
  Scale,
  UnitSystem,
} from '@ritabygglov/core';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { canvasService } from '../../../apiServices/CanvasService';
import Button, { ButtonType } from '../../../components/Button/Button';
import { FormatOptions } from '../../../components/Input/FormatOptions';
import { LengthFormatOptions } from '../../../components/Input/LengthFormatOptions';
import {
  Label,
  LabelLg,
  SelectInput,
  TextInput,
} from '../../../components/Input/Input';
import { Header, Paragraph } from '../../../components/Text/Text';
import {
  DEFAULT_FORMAT,
  DEFAULT_SCALE,
  SCALE_OPTIONS,
} from '../../../constants';
import { useModal } from '../../../context/Modal/useModal';
import { useToast } from '../../../context/Toast/useToast';
import { useUser } from '../../../context/User/useUser';
import {
  getDefaultLengthFormat,
  getDefaultUnitSystem,
} from './getDefaultUnitSystem';
import './NewProjectModal.css';

// 1" = 20', the common default for a US residential site plan.
const DEFAULT_IMPERIAL_SCALE = '1:240';

const OptionCard: React.FC<{
  type: DrawingType;
  heading: string;
  bodyText: string;
  imageUrl?: string;
  isEnabled: boolean;
  isPrechecked?: boolean;
  comingSoon: string;
}> = ({
  heading,
  bodyText,
  imageUrl,
  type,
  isEnabled,
  isPrechecked,
  comingSoon,
}) => {
  return (
    <label
      htmlFor={heading}
      className={`drawing-type-selector ${isEnabled ? 'enabled' : 'disabled'}`}
    >
      <div className="drawing-type-right-wrapper">
        <input
          type="radio"
          id={heading}
          name="drawingtype"
          value={type}
          disabled={!isEnabled}
          defaultChecked={isPrechecked}
        />
        <div>
          {!isEnabled && <span className="coming-soon">{comingSoon}</span>}
          <h4>{heading}</h4>
          <p>{bodyText}</p>
        </div>
      </div>

      {imageUrl ? (
        <img
          src={imageUrl}
          className="drawing-type-selector-img"
          alt="option illustration"
        />
      ) : (
        <div className="image-placeholder"></div>
      )}
    </label>
  );
};

export const NewProjectModal = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const modal = useModal();
  const { fetchUser, canvases } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [unitSystem, setUnitSystem] = useState<UnitSystem>(
    getDefaultUnitSystem(canvases)
  );
  const intl = useIntl();

  const DRAWING_TYPE = [
    {
      type: DrawingType.SITE_PLAN,
      title: intl.formatMessage({ id: 'drawingType.sitePlan' }),
      bodyText: intl.formatMessage({ id: 'drawingType.sitePlanText' }),
      imageUrl: 'situationsplan_img.png',
      isEnabled: true,
      isPrechecked: true,
    },
    {
      type: DrawingType.FACADE_DRAWING,
      title: intl.formatMessage({ id: 'drawingType.facadeDrawing' }),
      bodyText: intl.formatMessage({ id: 'drawingType.facadeDrawingText' }),
      imageUrl: 'fasadritning_img.png',
      isEnabled: true,
      isPrechecked: false,
    },
    {
      type: DrawingType.OTHER,
      title: intl.formatMessage({ id: 'drawingType.other' }),
      bodyText: intl.formatMessage({ id: 'drawingType.otherText' }),
      imageUrl: '',
      isEnabled: true,
      isPrechecked: false,
    },
    {
      type: DrawingType.FLOOR_PLAN,
      title: intl.formatMessage({ id: 'drawingType.floorPlan' }),
      bodyText: intl.formatMessage({ id: 'drawingType.floorPlanText' }),
      imageUrl: 'planritning_img.png',
      isEnabled: false,
      isPrechecked: false,
    },
  ];

  const SCALE_DESCRIPTIONS: Record<Scale, string> = {
    '1:1000': intl.formatMessage({ id: 'scaleDescription.1:1000' }),
    '1:800': intl.formatMessage({ id: 'scaleDescription.1:800' }),
    '1:500': intl.formatMessage({ id: 'scaleDescription.1:500' }),
    '1:400': intl.formatMessage({ id: 'scaleDescription.1:400' }),
    '1:250': intl.formatMessage({ id: 'scaleDescription.1:250' }),
    '1:200': intl.formatMessage({ id: 'scaleDescription.1:200' }),
    '1:100': intl.formatMessage({ id: 'scaleDescription.1:100' }),
    '1:50': intl.formatMessage({ id: 'scaleDescription.1:50' }),
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    setIsLoading(true);
    const data = new FormData(e.currentTarget);

    const form = {
      name: (data.get('name') || '') as string,
      pageFormat: (data.get('pageFormat') || '') as string,
      drawingHeadScale: (data.get('scale') || '') as string,
      drawingType: (data.get('drawingtype') || '') as string,
      unitSystem: (data.get('unitSystem') || 'metric') as string,
      lengthFormat: (data.get('lengthFormat') || 'meters') as string,
    };
    canvasService
      .createCanvas(form)
      .then(({ message, canvas }) => {
        toast.addToast({
          message,
          type: 'success',
        });
        fetchUser();
        if (canvas) {
          navigate('/projects/' + canvas.canvasId);
          modal.onClose();
        }
      })
      .catch((e: Error) => {
        toast.addToast({
          message: e.message,
          type: 'error',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="formContainer">
      <div className="formItem">
        <Header>{intl.formatMessage({ id: 'newProject.title' })}</Header>
        <Paragraph>
          {intl.formatMessage({ id: 'newProject.subtitle' })}
        </Paragraph>
      </div>
      <form onSubmit={onSubmit} className="form">
        <div className="formItem">
          <LabelLg htmlFor="name">
            {intl.formatMessage({ id: 'newProject.nameLabel' })}
          </LabelLg>
          <TextInput
            type="text"
            name="name"
            id="name"
            defaultValue={intl.formatMessage({ id: 'newProject.title' })}
            required
          />
        </div>
        <div className="formItem">
          <LabelLg htmlFor="name">
            {intl.formatMessage({ id: 'newProject.typeLabel' })}
          </LabelLg>
          {DRAWING_TYPE.map((item, index) => (
            <OptionCard
              key={index}
              heading={item.title}
              bodyText={item.bodyText}
              imageUrl={item.imageUrl}
              type={item.type}
              isEnabled={item.isEnabled}
              isPrechecked={item.isPrechecked}
              comingSoon={intl.formatMessage({ id: 'newProject.comingSoon' })}
            />
          ))}
        </div>
        <div className="formItem">
          <LabelLg htmlFor="unitSystem">
            {intl.formatMessage({ id: 'newProject.unitSystemLabel' })}
          </LabelLg>
          <SelectInput
            value={unitSystem}
            onChange={(e) => setUnitSystem(e.currentTarget.value as UnitSystem)}
            name="unitSystem"
            id="unitSystem"
            required
          >
            <option value="metric">
              {intl.formatMessage({ id: 'settings.unitSystemMetric' })}
            </option>
            <option value="imperial">
              {intl.formatMessage({ id: 'settings.unitSystemImperial' })}
            </option>
          </SelectInput>
          <SelectInput
            key={`lengthFormat-${unitSystem}`}
            defaultValue={getDefaultLengthFormat(canvases, unitSystem)}
            name="lengthFormat"
            id="lengthFormat"
            required
          >
            <LengthFormatOptions unitSystem={unitSystem} />
          </SelectInput>
        </div>
        <div className="formItem">
          <LabelLg>
            {intl.formatMessage({ id: 'newProject.scaleLabel' })}
          </LabelLg>
          <Label htmlFor="scale">
            <p>{intl.formatMessage({ id: 'newProject.scaleHint' })}</p>
            <p>
              <Link target="_blank" to="https://ritabygglov.se/guide">
                {intl.formatMessage({ id: 'newProject.scaleReadMore' })}
              </Link>
            </p>
          </Label>
          <SelectInput
            key={`scale-${unitSystem}`}
            defaultValue={
              unitSystem === 'imperial' ? DEFAULT_IMPERIAL_SCALE : DEFAULT_SCALE
            }
            name="scale"
            id="scale"
            required
          >
            {unitSystem === 'imperial'
              ? IMPERIAL_SCALE_OPTIONS.map((option) => (
                  <option value={option.ratio} key={option.ratio}>
                    {option.label}
                  </option>
                ))
              : SCALE_OPTIONS.map((scale) => (
                  <option value={scale} key={scale}>
                    {scale} - {SCALE_DESCRIPTIONS[scale]}
                  </option>
                ))}
          </SelectInput>
        </div>
        <div className="formItem">
          <LabelLg htmlFor="pageFormat">
            {intl.formatMessage({ id: 'newProject.formatLabel' })}
          </LabelLg>
          <Label>
            <p>{intl.formatMessage({ id: 'newProject.formatHint' })}</p>
          </Label>
          <SelectInput
            defaultValue={DEFAULT_FORMAT}
            name="pageFormat"
            id="pageFormat"
            required
          >
            <FormatOptions />
          </SelectInput>
        </div>
        <div className="formItem">
          <div>
            <Button
              type={ButtonType.Primary}
              htmlType="submit"
              loading={isLoading}
              loadingText={intl.formatMessage({
                id: 'newProject.submittingButton',
              })}
            >
              {intl.formatMessage({ id: 'newProject.submitButton' })}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
